import { useEffect, useState } from 'react';
import type { ServiceCode } from '../../../../../../common/enums/serviceCode';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ISelectOption } from '../../../../fields/Dropdown';
import type { IOrganization } from '../../../../interfaces/organization';
import type { ISpecialism } from '../../../../interfaces/specialism';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { getSpecialismSelectOptions } from '../../../../../utils/getSpecialismSelectOptions';
import { Dropdown } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';

interface ISpecialismOrganizationProviderProps {
    automation?: string;
    errors: ValidationErrors;
    initialOrganizationId: string | null;
    onChange: (organizationId: string | null) => Promise<void>;
    onDoValidate: () => void;
    serviceCode: ServiceCode;
}

export function SpecialismOrganizationProvider(props: ISpecialismOrganizationProviderProps) {
    const [specialismOptions, setSpecialismOptions] = useState<ISelectOption<string>[]>([]);
    const [selectedSpecialism, setSelectedSpecialism] = useState<string | null>(null);
    const [organizationOptions, setOrganizationOptions] = useState<ISelectOption<string>[]>([]);

    useEffect(() => {
        loadSpecialisms(props.serviceCode);
        if (props.initialOrganizationId) {
            loadOrganization(props.initialOrganizationId);
        }
    }, []);

    const loadSpecialisms = async (serviceCode: ServiceCode) => {
        const url = '/api/dhdThesaurus/specialisms/?providedService=' + serviceCode;
        const result = await fetchJson(url, 'GET');
        setSpecialismOptions(
            getSpecialismSelectOptions(result.json.items.filter((item: ISpecialism) => item.isActive))
        );
    };

    const loadOrganization = async (organizationId: string) => {
        const url = '/api/organizations/' + organizationId;
        const result = await fetchJson(url, 'GET');
        setOrganizationOptions([{ value: result.json.id, text: result.json.name }]);
    };

    const onChangeSpecialism = async (specialism: string | null) => {
        setSelectedSpecialism(specialism);
        setOrganizationOptions([]);
        await props.onChange(null);
        if (specialism) {
            const url = '/api/organizations/list';
            const result = await fetchJson(url, 'POST', {
                filters: {
                    providedService: props.serviceCode,
                    specialism: specialism,
                },
            });
            setOrganizationOptions(
                result.json.items.map((item: IOrganization) => {
                    return { value: item.id, text: item.name };
                })
            );
        }
    };

    return (
        <>
            <HorizontalFormGroup
                hasErrors={!selectedSpecialism && props.errors.has('selectedDiagnosticReportProviderOrganizationId')}
            >
                <FormLabel isRequired>{translateKey('service.specialism')}</FormLabel>
                <InputContainer>
                    <Dropdown
                        error={
                            selectedSpecialism
                                ? undefined
                                : props.errors.get('selectedDiagnosticReportProviderOrganizationId')
                        }
                        onChange={async (value) => await onChangeSpecialism(value)}
                        selectOptions={specialismOptions}
                        value={selectedSpecialism}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {(props.initialOrganizationId || selectedSpecialism) && (
                <HorizontalFormGroup hasErrors={props.errors.has('selectedDiagnosticReportProviderOrganizationId')}>
                    <FormLabel isRequired>{translateKey('request.sendTo')}</FormLabel>
                    <InputContainer>
                        <Dropdown
                            error={props.errors.get('selectedDiagnosticReportProviderOrganizationId')}
                            onChange={async (value) => await props.onChange(value)}
                            onDoValidate={async () => props.onDoValidate()}
                            placeholder="Klik en kies een organisatie..."
                            selectOptions={organizationOptions}
                            value={props.initialOrganizationId}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            )}
        </>
    );
}
